import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getBusOrderQuery } from '@/api/queries';
import { useTranslation } from 'react-i18next';
import { FirebaseClient } from '@/api/firebaseClient';
import { Container } from '@/components';
import { useMemo } from 'react';
import { useBus } from '@/hooks/useBus';

const client = new FirebaseClient();

export const BusOrderDetails = () => {
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  const { data } = useQuery(getBusOrderQuery(orderId ?? ''));
  const festivalEditionId =
    data?.owner?.kind === 'festivalEdition' ? data.owner.festivalEditionId : undefined;
  const { busses } = useBus(festivalEditionId);

  const ticketsWithBus = useMemo(() => {
    return (data?.tickets ?? []).map((ticket) => ({
      ...ticket,
      bus: busses?.find((bus) => bus.busId === ticket.busId),
    }));
  }, [data, busses]);

  if (!data) return null;

  const paid = Boolean(data.paidAt);

  const makePayment = async () => {
    const getPaymentUrl = await client.getPaymentUrl(data.orderId);
    location.href = getPaymentUrl.data.url;
  };

  if (data.canceledAt) {
    return (
      <Container max={760}>
        <h1 className="font-disp text-5xl text-ww-charcoal">BESTELLUNG STORNIERT</h1>
        <div
          className="mt-6 p-6"
          style={{
            background: 'var(--paper-50)',
            border: '2px solid var(--ww-orange)',
            boxShadow: '5px 5px 0 var(--ww-orange)',
          }}>
          <h2 className="font-sans text-xl font-bold text-ww-orange">{t('orderCanceled')}</h2>
          <p className="mt-2 font-sans text-ink-soft">{t('bus_ticket_canceled')}</p>
          <dl className="mt-4 font-mono text-sm">
            <dt className="text-ink-soft">{t('orderNumber')}</dt>
            <dd className="mb-2">{data.orderId}</dd>
            <dt className="text-ink-soft">E-Mail</dt>
            <dd>{data.email}</dd>
          </dl>
        </div>
      </Container>
    );
  }

  return (
    <Container max={920}>
      <div className="ww-eyebrow" style={{ color: 'var(--ww-orange)' }}>
        {t('orderStatus')}
      </div>
      <h1 className="mb-6 mt-2 font-disp text-5xl text-ww-charcoal">{t('bus_order')}</h1>

      {!paid ? (
        <div
          className="mb-7 flex flex-wrap items-center justify-between gap-5 p-6"
          style={{
            background: 'var(--ww-orange)',
            color: 'var(--paper-50)',
            border: '3px solid var(--ww-charcoal)',
            boxShadow: '5px 5px 0 var(--ww-charcoal)',
          }}>
          <div>
            <div className="font-sans text-[22px] font-extrabold uppercase tracking-[0.02em]">
              {t('paymentPending')}
            </div>
            <p className="mt-1 font-sans text-sm text-paper-100">{t('paymentPending_message')}</p>
          </div>
          <button
            type="button"
            onClick={makePayment}
            className="ww-btn shrink-0"
            style={{ background: 'var(--paper-50)' }}>
            {t('payNow')} →
          </button>
        </div>
      ) : null}

      <dl className="m-0">
        {paid ? (
          <div
            className="flex items-baseline justify-between gap-4 py-3"
            style={{ borderBottom: '1px solid var(--paper-300)' }}>
            <dt className="font-mono text-xs uppercase tracking-[0.08em] text-ink-soft">
              {t('paymentReceivedOn')}
            </dt>
            <dd className="m-0 font-sans text-lg font-medium text-ww-charcoal">
              {Intl.DateTimeFormat('de-DE').format(new Date(data.paidAt!))}
            </dd>
          </div>
        ) : null}
        <div
          className="flex items-baseline justify-between gap-4 py-3"
          style={{ borderBottom: '1px solid var(--paper-300)' }}>
          <dt className="font-mono text-xs uppercase tracking-[0.08em] text-ink-soft">
            {t('orderNumber')}
          </dt>
          <dd className="m-0 font-mono text-lg font-medium text-ww-charcoal">{data.orderId}</dd>
        </div>
        <div
          className="flex items-baseline justify-between gap-4 py-3"
          style={{ borderBottom: '1px solid var(--paper-300)' }}>
          <dt className="font-mono text-xs uppercase tracking-[0.08em] text-ink-soft">E-Mail</dt>
          <dd className="m-0 font-sans text-lg font-medium text-ww-charcoal">{data.email}</dd>
        </div>
        <div
          className="flex items-baseline justify-between gap-4 py-3"
          style={{ borderBottom: '1px solid var(--paper-300)' }}>
          <dt className="font-mono text-xs uppercase tracking-[0.08em] text-ink-soft">
            {t('orderPrice')}
          </dt>
          <dd className="m-0 font-mono text-3xl font-bold text-ww-orange">{data.price} €</dd>
        </div>
      </dl>

      <h2 className="mb-4 mt-8 font-sans text-lg font-bold uppercase tracking-[0.06em] text-ww-charcoal">
        Bus Tickets
      </h2>
      <div className="flex flex-col gap-3">
        {ticketsWithBus.map((ticket) => (
          <div
            key={ticket.busTicketId}
            className="flex items-center justify-between gap-4 p-4"
            style={{ background: 'var(--paper-50)', border: '2px solid var(--ww-charcoal)' }}>
            <div className="flex flex-wrap items-baseline gap-3.5">
              <Link
                to={`/bus_orders/${data.orderId}/${ticket.busTicketId}`}
                className="font-sans text-lg font-semibold text-ww-charcoal no-underline">
                {ticket.name}
              </Link>
              <span className="font-mono text-[13px] text-ink-soft">{ticket.bus?.label}</span>
            </div>
            <span className="font-mono text-xl font-bold text-ww-charcoal">
              {ticket.bus?.price} €
            </span>
          </div>
        ))}
      </div>
    </Container>
  );
};
